<template>
  <div v-if="aiSearchDealsLoading || dealResults?.length">
    <div
      ref="dealLinkWrapperRef"
      class="max-h-[370px] min-h-[100px] cursor-pointer space-y-3 overflow-y-auto px-4 pb-4 text-label"
      tabindex="-1"
    >
      <loader :is-loading="aiSearchDealsLoading" />
      <div
        v-for="result in dealResults"
        :key="result.id"
        ref="dealLinkRefs"
        :data-cy="`deal-link-${result.id}`"
        class="flex flex-row items-center space-x-4 rounded p-2"
        :class="{ 'bg-label/10': active === result.id }"
        @click="handleDealClick"
        @mouseover="active = result.id"
      >
        <icon-base
          :icon="IconDeals"
          view-box="10 10 20 20"
          class="text-label"
        />
        <div class="flex flex-col">
          <div class="flex items-center justify-start space-x-2">
            <span class="capitalize text-headline">
              {{ result.application_name }}
            </span>
            <lf-tag size="xs">{{ result.id?.slice(0, 7) }}&hellip;</lf-tag>
            <lf-pill
              size="sm"
              :color-index="getColorIndex(result.current_stage_category)"
            >
              {{ result.current_stage_category }}
            </lf-pill>
          </div>
          <div class="flex items-center justify-start space-x-1 text-xs">
            <span class="capitalize">{{ result.workflow_template_name }},</span>
            <span>
              {{ $t("LOGS.CREATED") }}:
              {{ formatDateCustom(result.created_at) }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="dealResults?.length"
      class="flex items-center space-x-4 border-t border-divider px-4 py-3"
    >
      <div class="flex items-center space-x-2">
        <div class="flex items-center space-x-1">
          <div class="icon-wrapper text-silver">
            <icon-base :icon="IconArrowLineUp" />
          </div>
          <div class="icon-wrapper text-silver">
            <icon-base :icon="IconArrowLineUp" class="rotate-180" />
          </div>
        </div>
        <span class="text-label">{{ $t("COMMON.NAVIGATE") }}</span>
      </div>
      <div class="flex items-center space-x-2">
        <div class="icon-wrapper text-silver">
          <icon-base :icon="IconArrowEnter" />
        </div>
        <span class="text-label">{{ $t("COMMON.OPEN") }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StageCategoryType } from "@/models/workflows";
import { formatDateCustom } from "@/helpers/formatting";
import { STAGE_TYPE_COLOR_CODES } from "@/helpers/constants";
import IconDeals from "@/components/icons/sidebar/IconDeals.vue";
import LfTag from "@/components/ui/text/LfTag.vue";
import Loader from "@/components/ui/Loader.vue";
import IconArrowLineUp from "@/components/icons/IconArrowLineUp.vue";
import IconArrowEnter from "@/components/icons/IconArrowEnter.vue";
import IconBase from "@/components/ui/IconBase.vue";
import { storeToRefs } from "pinia";
import { useAiSearchStore } from "@/stores/aiSearch";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import { useMagicKeys, whenever } from "@vueuse/core";
import { ref } from "vue";
import router from "@/router";

const emit = defineEmits<{
  "modal:close": [];
}>();

const store = useAiSearchStore();

const { arrowup, arrowdown, enter } = useMagicKeys();

const { dealResults, aiSearchDealsLoading } = storeToRefs(store);

const active = ref<string | null>(null);
const dealLinkWrapperRef = ref<HTMLDivElement | null>(null);
const dealLinkRefs = ref<HTMLDivElement[]>([]);

const getColorIndex = (category: string) =>
  STAGE_TYPE_COLOR_CODES[(category as StageCategoryType) || 0];

const navigate = () =>
  router.push({ name: ROUTE_DEAL_PROGRESS, params: { id: active.value } });

const handleDealClick = () => {
  if (!active.value) {
    return;
  }
  navigate();
  emit("modal:close");
};

whenever(arrowup, () => {
  if (!dealResults.value) {
    return;
  }

  dealLinkWrapperRef.value?.focus();

  if (!active.value) {
    active.value = dealResults.value[0].id;
    return;
  }

  const index = dealResults.value.findIndex(({ id }) => id === active.value);

  if (index === -1) {
    return;
  }

  const prevDealIndex = Math.max(0, index - 1);
  active.value = dealResults.value[prevDealIndex].id;
  dealLinkRefs.value[prevDealIndex].scrollIntoView({
    behavior: "smooth"
  });
});

whenever(arrowdown, () => {
  if (!dealResults.value) {
    return;
  }

  dealLinkWrapperRef.value?.focus();

  if (!active.value) {
    active.value = dealResults.value[0].id;
    return;
  }

  const index = dealResults.value.findIndex(({ id }) => id === active.value);

  if (index === -1) {
    return;
  }

  const nextDealIndex = Math.min(index + 1, dealResults.value.length - 1);
  active.value = dealResults.value[nextDealIndex].id;
  dealLinkRefs.value[nextDealIndex].scrollIntoView({
    behavior: "smooth"
  });
});

whenever(enter, () => {
  if (!active.value || !dealResults.value) {
    return;
  }
  handleDealClick();
});
</script>
<style scoped>
.icon-wrapper {
  @apply flex items-center justify-center rounded border border-divider p-1;
}
</style>
